import { useTranslation } from 'react-i18next';
import type { CatalogResponse } from '@/lib/api';
import { Swatch } from './Swatch';
import type { JSX } from 'react';

/**
 * Facets are grouped, never raw. `fabric` has 183 distinct values that fold to ~26
 * families, and `color` has 45 in ALL CAPS Polish which are display-cased and paired with
 * a swatch server side.
 */
export function Facets({
  facets,
  params,
  onChange,
  /** The mobile sheet supplies its own title and clear action, so it hides this one. */
  showHeader = true,
}: {
  facets: CatalogResponse['facets'] | undefined;
  params: URLSearchParams;
  onChange: (key: string, value: string | null) => void;
  showHeader?: boolean;
}): JSX.Element {
  const { t } = useTranslation();

  const toggle = (key: string, value: string): void => {
    const current = (params.get(key) ?? '').split(',').filter(Boolean);
    const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
    onChange(key, next.length > 0 ? next.join(',') : null);
  };
  const isOn = (key: string, value: string): boolean =>
    (params.get(key) ?? '').split(',').filter(Boolean).includes(value);

  return (
    <aside
      aria-label={t('catalog.filters')}
      data-testid="facets"
      className="space-y-7 lg:sticky lg:top-24 lg:self-start"
    >
      {/* `hidden` loses to Tailwind's `display:flex`, so the header is not rendered at all
          when the mobile sheet already provides its own title and clear action. */}
      {showHeader && (
        <div className="flex items-center justify-between">
          <p className="rz-eyebrow">{t('catalog.filters')}</p>
          {params.toString() && (
            <button
              type="button"
              onClick={() => onChange('__clear', null)}
              className="min-h-9 text-2xs underline underline-offset-2 text-[var(--color-muted)]"
            >
              {t('catalog.clearFilters')}
            </button>
          )}
        </div>
      )}

      {/*
        Price. The api has accepted `minPrice` and `maxPrice` all along and computes the
        range as a facet; the rail simply never offered it, so the most common filter on
        any furniture shop was the one filter missing.

        Two bounds rather than a slider: a slider on a range of 1,628 to 2,598 zł moves in
        jumps too coarse to be useful, and cannot be typed into.
      */}
      {facets?.priceRange && facets.priceRange.max > facets.priceRange.min && (
        <fieldset className="space-y-2.5">
          <legend className="mb-1 text-xs font-semibold">{t('catalog.price')}</legend>
          <div className="flex items-center gap-2">
            <label className="flex-1">
              <span className="sr-only">{t('catalog.priceFrom')}</span>
              <input
                type="number"
                inputMode="numeric"
                min={Math.floor(facets.priceRange.min / 100)}
                max={Math.ceil(facets.priceRange.max / 100)}
                placeholder={String(Math.floor(facets.priceRange.min / 100))}
                defaultValue={params.get('cena-od') ?? ''}
                onBlur={(e) => onChange('cena-od', e.target.value.trim() || null)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                }}
                className="rz-field !py-1.5 !text-xs"
              />
            </label>
            <span aria-hidden="true" className="text-xs text-[var(--color-muted)]">
              &ndash;
            </span>
            <label className="flex-1">
              <span className="sr-only">{t('catalog.priceTo')}</span>
              <input
                type="number"
                inputMode="numeric"
                min={Math.floor(facets.priceRange.min / 100)}
                max={Math.ceil(facets.priceRange.max / 100)}
                placeholder={String(Math.ceil(facets.priceRange.max / 100))}
                defaultValue={params.get('cena-do') ?? ''}
                onBlur={(e) => onChange('cena-do', e.target.value.trim() || null)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                }}
                className="rz-field !py-1.5 !text-xs"
              />
            </label>
          </div>
        </fieldset>
      )}

      {(facets?.colors.length ?? 0) > 0 && (
        <fieldset className="space-y-2.5">
          <legend className="text-xs font-semibold mb-1">{t('catalog.color')}</legend>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(2.75rem,1fr))] gap-1 justify-items-center">
            {facets?.colors.slice(0, 24).map((color) => (
              <button
                key={color.value}
                type="button"
                onClick={() => toggle('kolor', color.value)}
                aria-pressed={isOn('kolor', color.value)}
                title={`${color.label} (${color.count})`}
                className="inline-flex h-11 w-11 shrink-0 items-center justify-center"
              >
                <Swatch hex={color.hex ?? []} label={color.label} size={26} selected={isOn('kolor', color.value)} />
                <span className="sr-only">{color.label}</span>
              </button>
            ))}
          </div>
        </fieldset>
      )}

      {(facets?.fabricFamilies.length ?? 0) > 0 && (
        <fieldset className="space-y-1.5">
          <legend className="text-xs font-semibold mb-1">{t('catalog.fabricFamily')}</legend>
          <div className="flex flex-wrap gap-1.5" data-testid="fabric-facet">
            {facets?.fabricFamilies.slice(0, 30).map((family) => (
              <button
                key={family.value}
                type="button"
                onClick={() => toggle('tkanina', family.value)}
                aria-pressed={isOn('tkanina', family.value)}
                className="inline-flex min-h-9 items-center px-3 py-1 text-2xs font-medium capitalize transition-colors"
                style={{
                  background: isOn('tkanina', family.value) ? 'var(--color-ink)' : 'var(--color-ground-lift)',
                  color: isOn('tkanina', family.value) ? 'var(--color-ground)' : 'var(--color-ink)',
                }}
              >
                {family.label} <span className="opacity-50">{family.count}</span>
              </button>
            ))}
          </div>
        </fieldset>
      )}

      {(facets?.sizes.length ?? 0) > 0 && (
        <fieldset className="space-y-1.5">
          <legend className="text-xs font-semibold mb-1">{t('catalog.size')}</legend>
          <div className="flex flex-wrap gap-1.5">
            {facets?.sizes.slice(0, 12).map((size) => (
              <button
                key={size.value}
                type="button"
                onClick={() => toggle('rozmiar', size.value)}
                aria-pressed={isOn('rozmiar', size.value)}
                className="inline-flex min-h-9 items-center px-3 py-1 text-2xs font-medium tabular-nums transition-colors"
                style={{
                  background: isOn('rozmiar', size.value) ? 'var(--color-ink)' : 'var(--color-ground-lift)',
                  color: isOn('rozmiar', size.value) ? 'var(--color-ground)' : 'var(--color-ink)',
                }}
              >
                {size.value.replace(/-/g, '×')}
              </button>
            ))}
          </div>
        </fieldset>
      )}

      <fieldset className="space-y-1.5">
        <legend className="text-xs font-semibold mb-1">{t('catalog.availability')}</legend>
        {/* Radios with no "any" option trap the customer in a filter they cannot undo. */}
        <label className="flex min-h-9 items-center gap-2 text-xs">
          <input
            type="radio" name="dostepnosc"
            checked={!params.get('dostepnosc')}
            onChange={() => onChange('dostepnosc', null)}
          />
          {t('common.all')}
        </label>
        {(['in_stock', 'made_to_order'] as const).map((state) => (
          <label key={state} className="flex min-h-9 items-center gap-2 text-xs">
            <input
              type="radio"
              name="dostepnosc"
              checked={params.get('dostepnosc') === state}
              onChange={() => onChange('dostepnosc', state)}
            />
            {t(`availability.${state}`)}
          </label>
        ))}
      </fieldset>
    </aside>
  );
}
